import { useFundingNeedDocs } from '@/utils/hooks/fundingNeeds'
import {
  ListItem,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  ListItemText,
  Alert,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CachedIcon from '@mui/icons-material/Cached'

import { useQueryClient } from '@tanstack/react-query'
import { UUID } from 'crypto'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import RefetchConfirmDialog from '@/components/RefetchConfirmDialog'
import { AccessibleTooltip } from '@/components/AccessibleTooltip'
import { useUpdateOrgWebsiteMutation } from '@/utils/hooks/orgs'
import { isRootUrl } from '@/utils/utils'
import { localisedDate } from '@/utils/dateUtils'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'

interface FundingNeedDocListProps {
  orgUuid: UUID
  token: string
  fundingNeedId: number
  orgWebsite?: string | null
}

export default function FundingNeedDocList({
  orgUuid,
  token,
  fundingNeedId,
  orgWebsite,
}: FundingNeedDocListProps) {
  const updateOrgWebsite = useUpdateOrgWebsiteMutation()
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [refetchingId, setRefetchingId] = useState<number | null>(null)
  const [refetchError, setRefetchError] = useState<string | null>(null)
  const [refetchDialogOpen, setRefetchDialogOpen] = useState(false)
  const [pendingRefetchId, setPendingRefetchId] = useState<number | null>(null)
  const {
    data: docs,
    isLoading,
    deleteFundingNeedDoc,
    refetchFundingNeedWebsite,
    isError: docsError,
  } = useFundingNeedDocs(orgUuid, token as string, fundingNeedId)

  const queryClient = useQueryClient()

  const { canWrite } = usePausedOrg()

  const triggerRefetch = async (docId: number, fullCrawl: boolean) => {
    setRefetchError(null)
    setRefetchingId(docId)
    try {
      await refetchFundingNeedWebsite.mutateAsync({ token, docId, fullCrawl })
    } catch {
      setRefetchError(
        "Sorry, we couldn't get the latest content. Please try again.",
      )
    } finally {
      setRefetchingId(null)
    }
  }

  const handleRefetchClick = (docId: number, websiteUrl: string) => {
    if (isRootUrl(websiteUrl)) {
      setPendingRefetchId(docId)
      setRefetchDialogOpen(true)
    } else {
      triggerRefetch(docId, false)
    }
  }

  const handleSinglePageRefetch = () => {
    setRefetchDialogOpen(false)
    if (pendingRefetchId !== null) triggerRefetch(pendingRefetchId, false)
  }

  const handleFullCrawlRefetch = async (saveAsOrgWebsite: boolean) => {
    setRefetchDialogOpen(false)
    if (saveAsOrgWebsite) {
      const pendingDoc = docs?.find((d) => d.id === pendingRefetchId)
      if (pendingDoc?.website_url) {
        try {
          await updateOrgWebsite.mutateAsync({
            token,
            orgUuid,
            website: pendingDoc.website_url,
          })
        } catch {
          // Non-fatal
        }
      }
    }
    if (pendingRefetchId !== null) triggerRefetch(pendingRefetchId, true)
  }

  const handleDeleteDoc = async (docId: number) => {
    setDeleteError(null)

    // Show confirmation dialog
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this document? This action cannot be undone.',
    )

    if (!confirmDelete) {
      return
    }

    setDeletingId(docId)

    try {
      await deleteFundingNeedDoc.mutateAsync({
        token: token as string,
        docId: docId,
        needId: fundingNeedId,
      })
      queryClient.invalidateQueries({
        queryKey: ['fundingNeedDocs', orgUuid, fundingNeedId],
      })
    } catch {
      setDeleteError(
        "Sorry, we couldn't delete this document. Please try again.",
      )
    } finally {
      setDeletingId(null)
    }
  }

  if (isLoading) {
    return <CircularProgress />
  }

  if (docsError) {
    return <Alert severity="error">Error loading documents</Alert>
  }

  return (
    <Box sx={{ ml: -2, mb: 2 }}>
      <RefetchConfirmDialog
        open={refetchDialogOpen}
        onClose={() => setRefetchDialogOpen(false)}
        onSinglePage={handleSinglePageRefetch}
        onFullCrawl={handleFullCrawlRefetch}
        isLoading={refetchingId !== null}
        showSaveAsWebsite={!orgWebsite}
      />
      <ListItem sx={{ mb: -1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Webpages Uploaded for this Funding Request:
        </Typography>
      </ListItem>
      {docs && docs.filter((file) => file.website_url).length > 0 ? (
        docs
          .filter((file) => file.website_url !== null)
          .map((file) => (
            <ListItem
              sx={{ mb: -2 }}
              key={file.id}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <AccessibleTooltip title="Refresh content for this webpage">
                    <IconButton
                      disabled={refetchingId != null || deletingId != null}
                      onClick={() =>
                        handleRefetchClick(file.id, file.website_url as string)
                      }
                    >
                      {refetchingId === file.id ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CachedIcon />
                      )}
                    </IconButton>
                  </AccessibleTooltip>
                  <IconButton
                    edge="end"
                    disabled={deletingId != null || refetchingId != null}
                    onClick={() => handleDeleteDoc(file.id)}
                    sx={{ color: '#d32f2f' }}
                    aria-label={`Remove uploaded website: ${file.website_url}`}
                  >
                    {deletingId === file.id ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DeleteOutlineIcon />
                    )}
                  </IconButton>
                </Box>
              }
            >
              <Box>
                <Link
                  to={file.website_url as string}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {file.website_url}
                </Link>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Date Added:{' '}
                  {localisedDate(new Date(file.date_added), 'dd MMMM yyyy')}
                </Typography>
                {deletingId === file.id ? (
                  <Typography
                    variant="caption"
                    flexWrap={'wrap'}
                    width={'100%'}
                    color="#d32f2f"
                    ml={2}
                  >
                    Deleting...
                  </Typography>
                ) : null}
                {refetchingId === file.id ? (
                  <Typography variant="caption" color="text.secondary" ml={2}>
                    Updating...
                  </Typography>
                ) : null}
              </Box>
            </ListItem>
          ))
      ) : (
        <ListItem>
          <Typography>No websites uploaded</Typography>
        </ListItem>
      )}

      <ListItem sx={{ mb: -1, mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Files Uploaded for this Funding Request:
        </Typography>
      </ListItem>
      {docs && docs.filter((file) => !file.website_url).length === 0 ? (
        <ListItem>No files uploaded</ListItem>
      ) : (
        docs &&
        Array.isArray(docs) &&
        docs
          .filter((file) => !file.website_url)
          .map((file) => {
            const fileName = file.doc_url
              .replace(/^.*?(\d{8}_\d{6}_)/, '')
              .replace(/_/g, ' ')
            return (
              <ListItem
                key={file.id}
                sx={{ mb: -2 }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    disabled={deletingId != null || !canWrite}
                    onClick={() => handleDeleteDoc(file.id)}
                    sx={{ color: '#d32f2f' }}
                    aria-label={`Remove uploaded file: ${fileName}`}
                  >
                    {deletingId === file.id ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DeleteOutlineIcon />
                    )}
                  </IconButton>
                }
              >
                <Box>
                  <ListItemText primary={fileName} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Date Added:{' '}
                    {localisedDate(new Date(file.date_added), 'dd MMMM yyyy')}
                  </Typography>
                </Box>
              </ListItem>
            )
          })
      )}
      {deleteError && (
        <ListItem>
          <Alert severity="error" sx={{ width: '100%' }}>
            {deleteError}
          </Alert>
        </ListItem>
      )}
      {refetchError && (
        <ListItem>
          <Alert severity="error" sx={{ width: '100%' }}>
            {refetchError}
          </Alert>
        </ListItem>
      )}
    </Box>
  )
}
